import {
  ACCESSORIES_PROPERTIES,
  BIOMETRIC_DEVICE_TYPES,
  CHANNEL_EVENT_TYPES,
  DEVICE_TYPES,
  EVENT_STATUS,
  NOTIFICATION_MESSAGE_TYPE,
  USER_MEASUREMENTS,
} from '@/constants/device';
import {
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  getDayLabelOrDate,
  kilogramsToPounds,
  poundsToKilograms,
} from '@/utilities/helpers/commonHelper';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { VideoCallStatusUpdate } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { getDayLabel } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { Card, Skeleton } from 'antd';
import { get, isEmpty, isNil, omit } from 'lodash';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import {
  CallCompletedIcon,
  CallDeclinedIcon,
  CallFailedIcon,
  CallIcon,
  CallMissedIcon,
  CallScheduledIcon,
} from '../../icons';
import { CATEGORIES } from '../../utils';

export const RECENT_ACTIVITY_BIOMETRIC_CARD_TYPES = { ...BIOMETRIC_DEVICE_TYPES };

export const RECENT_ACTIVITY_SMART_HOME_CARD_TYPES = { ...omit(DEVICE_TYPES, ['COOKTOP_SENSOR']) };

export const RECENT_ACTIVITY_CARD_TYPES = {
  RECENT_CALLS: 'recent_calls',
  MOTION: 'motion',
  ENGAGEMENT: 'engagement',
  ...RECENT_ACTIVITY_BIOMETRIC_CARD_TYPES,
  ...RECENT_ACTIVITY_SMART_HOME_CARD_TYPES,
};

export const CATEGORY_CARD_MAPPING = {
  [CATEGORIES.SMART_HOME]: [...Object.values(RECENT_ACTIVITY_SMART_HOME_CARD_TYPES)],
  [CATEGORIES.MOTION]: [RECENT_ACTIVITY_CARD_TYPES.MOTION],
  [CATEGORIES.BIOMETRIC]: [...Object.values(RECENT_ACTIVITY_BIOMETRIC_CARD_TYPES)],
};

export const LoadingCard = () => (
  <Card className="recent-activity-card" variant="borderless">
    <Skeleton active round paragraph={{ rows: 3, width: '50%' }} />
  </Card>
);

export const LoadingModalContent = () => (
  <div className="recent-calls-loading">
    <Skeleton active round title={false} paragraph={{ rows: 7, width: '100%' }} />
  </div>
);

export const ErrorModalContent = () => {
  const trans = useSelector(translator);
  return <div className="recent-calls-empty">{trans('ERROR_MESSAGE')}</div>;
};

export const EmptyModalContent = () => {
  const trans = useSelector(translator);
  return <div className="recent-calls-empty">{trans('NODATAAVAIALBLE')}</div>;
};

export const getDateLabel = (isoTimestamp, trans, timezone, locale = 'en-US') => {
  if (!isoTimestamp) return '';

  const tzOptions = timezone ? { timeZone: timezone } : {};
  const date = new Date(isoTimestamp);

  // Get today and yesterday in the user's timezone using moment
  const today = timezone ? moment().tz(timezone).startOf('day') : moment().startOf('day');
  const eventDate = timezone
    ? moment(isoTimestamp).tz(timezone).startOf('day')
    : moment(isoTimestamp).startOf('day');
  const yesterday = today.clone().subtract(1, 'day');

  if (eventDate.isSame(today, 'day')) return trans('TODAY').trim();

  if (eventDate.isSame(yesterday, 'day')) return trans('YESTERDAY');

  return date.toLocaleDateString(locale, {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    ...tzOptions,
  });
};

export const formatTime = (isoTimestamp, is24Hours = false, timezone) => {
  if (!isoTimestamp) return '';
  const date = new Date(isoTimestamp);
  return date.toLocaleTimeString([], {
    hour: is24Hours ? '2-digit' : 'numeric',
    minute: '2-digit',
    hour12: !is24Hours,
    ...(timezone ? { timeZone: timezone } : {}),
  });
};

export const sortByLatestTimestamp = (list = [], timestampKey = 'utc_timestamp_inms') => {
  return [...list].sort((a, b) => {
    const timeA = new Date(a?.[timestampKey] || 0).getTime();
    const timeB = new Date(b?.[timestampKey] || 0).getTime();
    return timeB - timeA;
  });
};

/**
 * Determines whether a scheduled call is currently in progress by building the
 * call window from the start instant (utc_timestamp_inms / event_timestamp) and
 * the event_starttime / event_endtime time-of-day strings.
 */
export const isCallInProgress = (notification) => {
  const startInstant = notification?.utc_timestamp_inms || notification?.event_timestamp;
  if (!startInstant) return false;

  const startMoment = moment(startInstant);
  if (!startMoment.isValid()) return false;

  const toSeconds = (time) => {
    if (!time) return null;
    const [hours = 0, minutes = 0, seconds = 0] = String(time).split(':').map(Number);
    if ([hours, minutes, seconds].some((part) => Number.isNaN(part))) return null;
    return hours * 3600 + minutes * 60 + seconds;
  };

  const startSeconds = toSeconds(notification?.event_starttime);
  const endSeconds = toSeconds(notification?.event_endtime);

  let endMoment;
  if (startSeconds !== null && endSeconds !== null) {
    endMoment = startMoment.clone().add(endSeconds - startSeconds, 'seconds');
  } else if (notification?.duration_minutes) {
    endMoment = startMoment.clone().add(Number(notification.duration_minutes), 'minutes');
  } else {
    return false;
  }

  const now = moment();
  return now.isSameOrAfter(startMoment) && now.isBefore(endMoment);
};

/**
 * Merges notification call records (missed/declined/completed) with schedule events,
 * deduplicates by schedule_eventuuid, normalises timestamps, and sorts newest-first.
 */
export const mergeCallData = (notifications, scheduleEvents) => {
  const filteredNotifications = Object.values(
    (notifications || [])
      .filter(
        (e) =>
          e.message_type === NOTIFICATION_MESSAGE_TYPE.CALL_STATUS &&
          (e.call_status__status === VideoCallStatusUpdate.missed ||
            e.call_status__status === VideoCallStatusUpdate.declined ||
            e.call_status__status === VideoCallStatusUpdate.completed)
      )
      .reduce((acc, n) => {
        const key =
          n.meeting_id ||
          n.schedule_eventuuid ||
          `${n.utc_timestamp_inms ?? ''}_${n.useruuid ?? ''}`;
        const toTs = (v) => {
          const ms = Number(v);
          return isFinite(ms) ? ms : -Infinity;
        };
        if (!acc[key] || toTs(n.utc_timestamp_inms) > toTs(acc[key].utc_timestamp_inms)) {
          acc[key] = n;
        }
        return acc;
      }, {})
  );

  // Track schedule_eventuuids already represented by a notification (completed calls)
  const notifScheduleUuids = new Set(
    filteredNotifications.map((n) => n.schedule_eventuuid).filter(Boolean)
  );

  // Normalise schedule events: only include resolved events (completed/expired/ongoing),
  // skip duplicates already covered by a notification
  const normalizedScheduleEvents = (scheduleEvents || [])
    .filter((e) => Object.values(EVENT_STATUS).includes(e.event_status))
    .filter((e) => e.schedule_eventuuid && !notifScheduleUuids.has(e.schedule_eventuuid))
    .map((e) => ({
      ...e,
      utc_timestamp_inms: e.utc_timestamp_inms || e.event_timestamp,
    }));

  return sortByLatestTimestamp([...filteredNotifications, ...normalizedScheduleEvents]);
};

export const getContactName = (notification) => {
  const invitedContacts = notification?.invited_contacts;
  const userUuid = notification?.useruuid;

  if (!invitedContacts || !Array.isArray(invitedContacts) || invitedContacts.length === 0) {
    return null;
  }

  const contact =
    invitedContacts.find((c) => c?.useruuid && c.useruuid !== userUuid) || invitedContacts[0];
  const firstName = contact?.firstname || '';
  const lastName = contact?.lastname || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || null;
};

export const getCallTitle = (notification, trans, useruuid) => {
  if (notification?.event_name) {
    return notification.event_name;
  }

  const invitedContacts = notification?.invited_contacts;
  const userUuid = useruuid || notification?.useruuid;
  const otherContact =
    Array.isArray(invitedContacts) && invitedContacts.length > 0
      ? invitedContacts.find((c) => c?.useruuid && c.useruuid !== userUuid) || invitedContacts[0]
      : null;
  const firstName = otherContact?.firstname || '';
  const lastName = otherContact?.lastname || '';
  const fullName = `${firstName} ${lastName}`.trim() || null;

  return fullName || trans('VIDEOCALL');
};

export const getStatusInfo = (status, trans) => {
  // Schedule events have no call_status — show as Scheduled
  if (!status) {
    return {
      text: trans('SCHEDULED'),
      className: 'info',
      icon: <CallScheduledIcon className="recent-calls-status-icon" />,
    };
  }

  if (status === VideoCallStatusUpdate.completed) {
    return {
      text: trans('COMPLETED'),
      className: 'success',
      icon: <CallCompletedIcon className="recent-calls-status-icon" />,
    };
  }

  if (status === VideoCallStatusUpdate.declined) {
    return {
      text: trans('DECLINED'),
      className: 'declined',
      icon: <CallDeclinedIcon className="recent-calls-status-icon" />,
    };
  }

  if (status === VideoCallStatusUpdate.missed) {
    return {
      text: trans('MISSED'),
      className: 'danger',
      icon: <CallMissedIcon className="recent-calls-status-icon" />,
    };
  }

  if (status === EVENT_STATUS.ONGOING) {
    return {
      text: trans('INPROGRESS'),
      className: 'ongoing',
      icon: <CallIcon className="recent-calls-status-icon" fill="#113e57" />,
    };
  }

  if (
    status === VideoCallStatusUpdate.calling ||
    status === VideoCallStatusUpdate.invited ||
    status === VideoCallStatusUpdate.ringing ||
    status === VideoCallStatusUpdate.accepted ||
    status === VideoCallStatusUpdate.joining
  ) {
    return {
      text: trans('SCHEDULED'),
      className: 'info',
      icon: <CallScheduledIcon className="recent-calls-status-icon" />,
    };
  }

  return {
    text: trans('FAILED'),
    className: 'danger',
    icon: <CallFailedIcon className="recent-calls-status-icon" />,
  };
};

export const getChannelType = (messageType) => {
  switch (messageType.toLowerCase()) {
    case CHANNEL_EVENT_TYPES.CHANNEL_LAUNCHED:
      return 'Launched';
    case CHANNEL_EVENT_TYPES.VIDEO_PLAY_STARTED:
      return 'Started';
    case CHANNEL_EVENT_TYPES.VIDEO_PLAY_COMPLETED:
      return 'Completed';
    case CHANNEL_EVENT_TYPES.VIDEO_PLAY_ABANDONED:
      return 'Abandoned';
    case CHANNEL_EVENT_TYPES.VIDEO_PLAY_ERROR:
      return 'Error';
    default:
      return '-';
  }
};

export const reduceAccessoryToMotionDevice = (accessory) => {
  if (isEmpty(accessory)) return {};

  const modelnumber = get(accessory, ACCESSORIES_PROPERTIES.MODEL_NUMBER, '');
  const serialnumber = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_SERIAL_NUMBER, '');
  const location = get(accessory, ACCESSORIES_PROPERTIES.LOCATION, '');
  const battery_level = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_BATTERY_LEVEL, '');
  const paired_status = get(accessory, ACCESSORIES_PROPERTIES.PAIRED_STATUS, '');
  const deviceconnectstatus = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_CONNECT_STATUS, '');
  const device_connected = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_CONNECTED, '');
  const connectionStatus =
    modelnumber === 'HC200'
      ? deviceconnectstatus
      : paired_status === 'UNPAIRED'
        ? false
        : device_connected
          ? 'CONNECTED'
          : 'DISCONNECTED';

  return { serialnumber, modelnumber, location, battery_level, connectionStatus };
};

export const formatTimestamp = (timestamp, timezone, locale, is24HourFormat, trans) => {
  if (timestamp == null || timestamp == undefined) {
    return { day: '-', time: '-', difference: '-', formattedDate: '-' };
  }
  const now = moment().tz(timezone);
  const inputTime = moment.tz(timestamp, timezone);

  const dayLabel = getDayLabel(timestamp, timezone);

  let time = '';
  let formattedDate = '';
  let day = '';

  if (dayLabel === 'Today' || dayLabel === 'Yesterday') {
    day = dayLabel;
  } else {
    formattedDate = getDayLabelOrDate(timestamp, timezone, locale);
    day = formattedDate;
  }

  if (is24HourFormat) {
    time = inputTime.format('HH:mm');
  } else {
    time = inputTime.format('h:mm A');
  }

  if (!dayLabel) {
    formattedDate = getDayLabelOrDate(timestamp, timezone, locale);
  }
  const difference = moment.duration(inputTime?.diff(now));
  let differenceText;

  if (difference.asMilliseconds() < 0) {
    const pastDifference = moment.duration(now?.diff(inputTime));
    differenceText =
      pastDifference.asMinutes() < 1
        ? trans('JUST_NOW_L')
        : pastDifference.asMinutes() < 60
          ? `${Math.round(pastDifference.asMinutes())} ${trans('MINAGO_L')} `
          : pastDifference.asHours() < 24
            ? `${Math.round(pastDifference.asHours())} ${trans('HOURSAGO_L')} `
            : `${Math.round(pastDifference.asDays())} ${trans('DAYSAGO_L')} `;
  } else {
    differenceText =
      difference.asMinutes() < 1
        ? trans('LATER')
        : difference.asMinutes() < 60
          ? `${trans('IN')} ${Math.round(difference.asMinutes())} ${trans('MIN_L')} `
          : difference.asHours() < 24
            ? `${trans('IN')} ${Math.round(difference.asHours())} ${trans('HOURS_L')} `
            : `${trans('IN')} ${Math.round(difference.asDays())} ${trans('DAYS_L')} `;
  }

  return {
    day,
    time,
    difference: differenceText,
    formattedDate,
  };
};

export const temperatureUnits = { metric: '°C', imperial: '°F' };

export const weightUnits = { metric: 'kgs', imperial: 'lbs' };

export const convertTemperature = (value, currentMeasurementSystem, targetMeasurementSystem) => {
  if (isNil(value) || value === '' || value === '-') return '-';
  if (currentMeasurementSystem === targetMeasurementSystem)
    return Math.round(Number(value).toFixed(1));

  return targetMeasurementSystem === USER_MEASUREMENTS.METRIC
    ? Math.round(fahrenheitToCelsius(Number(value).toFixed(1)))
    : Math.round(celsiusToFahrenheit(Number(value).toFixed(1)));
};

export const convertWeight = (value, currentMeasurementSystem, targetMeasurementSystem) => {
  if (isNil(value) || value === '' || value === '-') return '-';
  if (currentMeasurementSystem === targetMeasurementSystem) return Number(value).toFixed(1);

  return targetMeasurementSystem === USER_MEASUREMENTS.METRIC
    ? poundsToKilograms(Number(value)).toFixed(1)
    : kilogramsToPounds(Number(value)).toFixed(1);
};
